import { useMemo, useCallback, useContext, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { useLazyListPassesQuery } from '../../../store/redux/api/passApi';
import {
  selectPasses,
  selectFilterValues,
  selectSortValues,
  mergePasses,
} from '../../../store/redux/slices/passList.slice';
import { MeContext } from '../../../context/MeContext';
import PassesListTable from './PassesListTable';
import type {
  PassListItem,
  PassContent,
  NormalisedPassListItem,
} from '../../../store/types/pass';
import type { MeContextState } from '../../../store/types/account';
import { getTimezoneAbbr } from './utils/passHelpers';

const hasLocalisation = (passContent: PassContent | undefined): boolean => {
  if (!passContent || typeof passContent !== 'object') {
    return false;
  }

  return Object.values(passContent).some((field) => {
    return (
      field &&
      typeof field === 'object' &&
      Object.prototype.hasOwnProperty.call(field, 'localisation') &&
      (field as { localisation: Record<string, string> }).localisation &&
      Object.keys(
        (field as { localisation: Record<string, string> }).localisation
      ).length > 0
    );
  });
};

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatTimestamp = (
  dateStr: string | null | undefined,
  fallback = 'n/a'
): string => {
  if (!dateStr) return fallback;
  const timestamp = new Date(dateStr);
  const day = timestamp.getDate();
  const month = timestamp.toLocaleString('en-GB', { month: 'short' });
  const year = String(timestamp.getFullYear()).slice(-2);
  const hours = timestamp.getHours();
  const minutes = String(timestamp.getMinutes()).padStart(2, '0');
  const tzAbbr = getTimezoneAbbr(LOCAL_TZ, timestamp);
  return `${month} ${day} ${year}, ${hours}.${minutes} ${tzAbbr}`;
};

const PASS_TYPE_LABELS: Record<string, string> = {
  storeCard: 'Loyalty',
  eventTicket: 'Event ticket',
  generic: 'Generic',
  transit: 'Transit',
  stampCard: 'Stamp card',
};

const normaliseData = (data: PassListItem[]): NormalisedPassListItem[] => {
  if (!data?.length) return [];

  return data.map((item) => ({
    ...item,
    udi: item.attributes.extId,
    passUuid: item.id,
    passType:
      PASS_TYPE_LABELS[item.attributes.passType] || item.attributes.passType,
    templateName: item.attributes.passTemplateName,
    issuedAt: formatTimestamp(item.attributes.createdAt),
    deviceType: item.attributes.deviceType
      ? item.attributes.deviceType === 'iphone'
        ? 'Apple'
        : 'Android'
      : 'n/a',
    addedToWallet: formatTimestamp(item.attributes.addedAt, 'Not yet added'),
    hasLocalisation: hasLocalisation(item.attributes.passContent),
  }));
};

const PassesList = () => {
  const dispatch = useAppDispatch();
  const passes = useAppSelector(selectPasses);
  const filterValues = useAppSelector(selectFilterValues);
  const sortValues = useAppSelector(selectSortValues);

  const meCtx = useContext(MeContext) as { state: MeContextState };
  const { filterEntity } = meCtx.state;

  const [triggerListPasses] = useLazyListPassesQuery();
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(
    async ({
      pageSize,
      pageIndex,
    }: {
      pageSize: number;
      pageIndex: number;
    }) => {
      const fetchId = ++fetchIdRef.current;
      dispatch(mergePasses({ loading: true, status: 'pending' }));

      try {
        const result = await triggerListPasses({
          entityId: filterEntity?.uuid,
          page: pageIndex + 1,
          perPage: pageSize,
          filters: filterValues,
          sortBy: sortValues.sortBy || undefined,
          sortOrder: sortValues.sortOrder || undefined,
        }).unwrap();

        if (fetchId !== fetchIdRef.current) return;

        const items = result?.data || [];
        const total = result?.meta?.totalCount ?? 0;

        dispatch(
          mergePasses({
            items: normaliseData(items),
            total,
            pageCount: Math.ceil(total / pageSize),
            pageIndex,
            pageSize,
            loading: false,
            status: 'resolved',
            error: null,
          })
        );
      } catch {
        if (fetchId !== fetchIdRef.current) return;

        dispatch(
          mergePasses({
            items: [],
            total: 0,
            pageCount: 0,
            loading: false,
            status: 'rejected',
            error: 'Something went wrong',
          })
        );
      }
    },
    [dispatch, filterEntity, filterValues, sortValues, triggerListPasses]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Pass ID',
        accessor: 'externalId',
        className: 'modeTdColor',
        style: {
          wordBreak: 'break-word',
          width: '280px',
        },
      },
      {
        Header: 'Template',
        accessor: 'passTemplate',
        style: {
          wordBreak: 'break-word',
        },
      },
      {
        Header: 'Added to wallet',
        accessor: 'addedToWallet',
        style: {
          whiteSpace: 'nowrap',
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        style: {},
      },
      {
        Header: 'Actions',
        accessor: 'actions',
      },
    ],
    []
  );

  const query = useMemo(() => {
    const keyMap: Record<string, string> = {
      id: 'uuid',
      extId: 'externalId',
      extIdPrefix: 'extIdPrefix',
      passTemplate: 'passTemplateUuid',
      groupTag: 'groupTags[]',
      passType: 'passType',
      deviceType: 'deviceType',
      status: 'status',
      search: 'search',
    };

    return Object.entries(filterValues)
      .filter(([, value]) =>
        Array.isArray(value) ? value.length > 0 : value !== ''
      )
      .map(([key, value]) => {
        const paramKey = keyMap[key] || key;
        if (Array.isArray(value)) {
          return value.map((v) => `&${paramKey}=${v}`).join('');
        }
        return `&${paramKey}=${value}`;
      })
      .join('');
  }, [filterValues]);

  const formattedData = useMemo(
    () => normaliseData(passes.items as unknown as PassListItem[]),
    [passes.items]
  );

  return (
    <PassesListTable
      columns={columns}
      data={formattedData}
      query={query}
      fetchData={fetchData}
      isLoading={passes.loading}
      pageCount={passes.pageCount}
      total={passes.total}
    />
  );
};

export default PassesList;
